import React, { useState, useEffect } from 'react';
import { RefreshCw, Play, CheckCircle, XCircle, Clock, Loader, Database } from 'lucide-react';
import toast from 'react-hot-toast';
import { backupService, BackupHistory, BackupStats } from '../../services/backupService';

const BackupHistoryTable: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [history, setHistory] = useState<BackupHistory[]>([]);
  const [stats, setStats] = useState<BackupStats | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadHistory();
    loadStats();
  }, [page]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await backupService.getHistory(page + 1, rowsPerPage);
      setHistory(response.results);
      setTotalCount(response.count);
    } catch (error) {
      console.error('Failed to load history:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await backupService.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleTriggerBackup = async () => {
    setTriggering(true);

    try {
      const result = await backupService.triggerBackup();
      toast.success(`Backup started. Task: ${result.task_id}`);

      setTimeout(() => {
        loadHistory();
        loadStats();
      }, 2000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to trigger backup');
    } finally {
      setTriggering(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      running: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Backup History</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              loadHistory();
              loadStats();
            }}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={handleTriggerBackup}
            disabled={triggering}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {triggering ? (
              <Loader className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            {triggering ? 'Starting...' : 'Start Backup Now'}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Total Backups</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_backups}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Successful</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{stats.successful_backups}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Failed</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{stats.failed_backups}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Total Size</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {(stats.total_size_bytes / (1024 * 1024 * 1024)).toFixed(2)} GB
            </p>
          </div>
        </div>
      )}

      {/* History Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Files</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                </td>
              </tr>
            ) : history.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No backup history found. Start your first backup!
                </td>
              </tr>
            ) : (
              history.map((backup) => (
                <tr key={backup.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(backup.status)}`}>
                      {backup.status === 'success' && <CheckCircle className="w-3 h-3 mr-1" />}
                      {backup.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                      {backup.status === 'running' && <Loader className="w-3 h-3 mr-1 animate-spin" />}
                      {backup.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{backup.backup_type}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(backup.started_at)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {backup.duration_formatted || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Database className="w-4 h-4 mr-1" />
                      {backup.files_backed_up}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{backup.size_formatted}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalCount > rowsPerPage && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={(page + 1) * rowsPerPage >= totalCount}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Latest Backup Info */}
      {stats?.latest_success && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-900">
            <strong>Latest successful backup:</strong> {formatDate(stats.latest_success.started_at)} - {stats.latest_success.files_backed_up} files ({stats.latest_success.size_formatted})
          </p>
        </div>
      )}
    </div>
  );
};

export default BackupHistoryTable;
